/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { RichTextComponent } from '@msdyn365-commerce/core';

import * as React from 'react';
import classname from 'classnames';
import { useInView } from 'react-intersection-observer';

interface IProductData {
    image: JSX.Element | null;
    price: number | undefined;
    carouselIndex: number;
    description: string | undefined;
    ratingComp: JSX.Element | null;
    promoBadge: JSX.Element | undefined;
    linkTo: string | undefined;
    productID: string | undefined;
    productCategory: string | undefined;
    productBrand: string | undefined;
    productVariant: string | undefined;
    enhancedEcommTracking: string | undefined;
    productListName: string | undefined;
    deliveryMessage: string | undefined;
    availableInStoreMessage: string | undefined;
    productInventoryLoadStatus: boolean;
}

export default (props: IProductData) => {
    const styles = 'mfrm-recommendation-module__';

    const {
        image,
        description,
        promoBadge,
        linkTo,
        price,
        ratingComp,
        productBrand,
        productCategory,
        productID,
        carouselIndex,
        productVariant,
        enhancedEcommTracking,
        productListName,
        deliveryMessage,
        availableInStoreMessage,
        productInventoryLoadStatus
    } = props;

    const listProductName = productListName ? productListName : 'Most Popular Mattresses Module';

    const handleClick = () => {
        // @ts-ignore
        if ('utag' in window) {
            //@ts-ignore
            utag.link({
                event: 'product-click',
                enhanced_action: 'product_click',
                event_category: 'ecommerce',
                event_action: 'product click',
                event_label: `${productID}: ${description}`,
                event_noninteraction: 'false',
                product_list_name: [listProductName],
                product_position: [carouselIndex + 1],
                product_id: [productID],
                product_name: [description],
                product_category: [productCategory],
                product_brand: [productBrand],
                product_variant: [productVariant]
            });
        }
        if (MsDyn365.isBrowser && linkTo) {
            window.location.href = linkTo;
        }
    };

    const productRecommendationref = React.useRef<any>();
    React.useEffect(() => {
        productCategory && productRecommendationref.current && productRecommendationref.current.addEventListener('click', handleClick);
    }, [productCategory]);

    const [productRecommendationViewRef, inView] = useInView({
        triggerOnce: true,
        rootMargin: '25px'
    });

    const isProduct = enhancedEcommTracking === 'product';

    React.useEffect(() => {
        if (isProduct && inView && productCategory) {
            const pagePath = MsDyn365.isBrowser
                ? window.location.href.split(location.host)[1].includes('?')
                    ? window.location.href.split(location.host)[1].split('?')[0]
                    : window.location.href.split(location.host)[1]
                : '';
            //@ts-ignore
            utag.link({
                event: 'product-impression',
                event_category: 'ecommerce',
                event_action: 'product impression',
                event_label: `${pagePath}`,
                event_noninteraction: 'true',
                product_impression_list_name: [listProductName],
                product_impression_position: [carouselIndex + 1],
                product_impression_id: [productID],
                product_impression_name: [description],
                product_impression_category: [productCategory],
                product_impression_brand: [productBrand],
                product_impression_variant: [productVariant],
                product_impression_price: [price]
            });
        }
    }, [inView, productCategory]);

    const devlieryIndicator = deliveryMessage !== 'Delivery in 6+ Weeks' ? <div className='mfrm-recommendation-module__availabile-indicator'/>
                              : <div className='mfrm-recommendation-module__availabile-indicator mfrm-recommendation-module__not-availabile-indicator'/>;
    const availableInStoreIndicator = availableInStoreMessage?.slice(0,9) === 'Available' ? <div className='mfrm-recommendation-module__availabile-indicator'/>
                              : <div className='mfrm-recommendation-module__availabile-indicator mfrm-recommendation-module__not-availabile-indicator'/>;

    return (
        <div ref={productRecommendationref} className={`${styles}parent`}>
            <a ref={productRecommendationViewRef} className={`${styles}link`} href={linkTo}>
                <div className={classname(`${styles}sale`)}>
                    <div className={classname(`${styles}imagecontainer`)}>
                        <div className={classname(`${styles}promobadge`)}>{promoBadge}</div>
                        <div className={classname(`${styles}image`)}>{image}</div>
                    </div>
                    <div className={classname(`${styles}text-container`)}>
                        <div className={classname(`${styles}title`)}>{price && <RichTextComponent text={`Starting at $${price}`} />}</div>
                        <div className={classname(`${styles}description`)}>
                            <RichTextComponent text={description} />
                        </div>
                    </div>
                    <div className={classname(`${styles}rating-container`)}>{ratingComp}</div>
                    {productInventoryLoadStatus &&
                    <>
                        <div className='mfrm-recommendation-module__availability-text-container'>
                            {devlieryIndicator}
                            {deliveryMessage}
                        </div>
                        <div className='mfrm-recommendation-module__availability-text-container'>
                            {availableInStoreIndicator}
                            {availableInStoreMessage}
                        </div>
                    </>
                    }
                </div>
            </a>
        </div>
    );
};

